import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  HomeIcon, 
  UserIcon, 
  ClockIcon,
  ShoppingBagIcon,
  ChartBarIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const Layout = ({ children, user }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Leads', href: '/leads', icon: UserIcon },
    { name: 'Follow Ups', href: '/follow-ups', icon: ClockIcon },
    { name: 'Orders', href: '/orders', icon: ShoppingBagIcon },
    { name: 'Reports', href: '/reports', icon: ChartBarIcon },
  ];

  const handleSignOut = async () => {
    if (signingOut) return; // Prevent multiple clicks
    
    try {
      setSigningOut(true);
      console.log('Starting sign out process...');
      
      // Try to sign out, but handle session missing gracefully
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Sign out error:', error);
        
        // If session is missing, that's actually fine - user is already signed out
        if (error.message.includes('Auth session missing') || error.message.includes('session')) {
          console.log('Session already expired, proceeding with sign out');
          toast.success('Signed out successfully');
        } else {
          console.log('Other error occurred, but proceeding with manual sign out');
          toast.success('Signed out successfully');
        }
      } else {
        console.log('Sign out successful');
        toast.success('Signed out successfully');
      }
      
    } catch (error) {
      console.error('Unexpected sign out error:', error);
      console.log('Error occurred, but proceeding with manual sign out');
      toast.success('Signed out successfully');
    }
    
    // Always clear local storage and reload regardless of error
    console.log('Clearing local storage and reloading...');
    try {
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear Supabase auth tokens specifically
      const supabaseKeys = Object.keys(localStorage).filter(key => key.includes('supabase'));
      supabaseKeys.forEach(key => localStorage.removeItem(key));
      
      const supabaseSessionKeys = Object.keys(sessionStorage).filter(key => key.includes('supabase'));
      supabaseSessionKeys.forEach(key => sessionStorage.removeItem(key));
      
    } catch (clearError) {
      console.error('Error clearing storage:', clearError);
    }
    
    // Force reload to ensure clean state
    setTimeout(() => {
      window.location.href = '/';
    }, 1000);
    
    setSigningOut(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200">
            <h1 className="text-xl font-semibold text-gray-900">CRMOne</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4 border-b border-gray-200">
            <h1 className="text-xl font-semibold text-gray-900">CRMOne</h1>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <button className="text-gray-400 hover:text-gray-500">
                <BellIcon className="h-6 w-6" />
              </button>
              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" />
              <div className="flex items-center gap-x-4">
                <div className="flex items-center gap-x-2">
                  <UserCircleIcon className="h-8 w-8 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">
                    {user?.email}
                  </span>
                </div>
                <button
                  onClick={handleSignOut}
                  disabled={signingOut}
                  className={`text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed ${
                    signingOut ? 'animate-pulse' : ''
                  }`}
                >
                  {signingOut ? 'Signing out...' : 'Sign out'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;

